/**
 * MenuListContainer
 * --
 */
import React, { useEffect } from 'react';
import MenuListPresenter from './MenuListPresenter';
import {
  useCreateMenu,
  useGetMenus,
  useUpdateMenu,
  useDeleteMenu,
  menuQueryKeys,
} from 'hooks/MenuHooks';
import { useCreateMenuFile } from 'hooks/MenuFileHooks';
import { useFileUpload } from 'hooks/FileHooks';
import {
  useCreateProductCategory,
  useGetMenuCategories,
  useUpdateProductCategory,
  productCategoryQueryKeys,
} from 'hooks/ProductCategoryHooks';
import { LoadingPage } from 'components';
import MessageAlert from 'utils';
import { useUserStack } from 'store';
import { useQueryClient } from '@tanstack/react-query';
import { useGetGrades } from 'hooks/GradeHooks';

const MenuListContainer = () => {
  /* ===== ROUTE ===== */
  const queryClient = useQueryClient();
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const { createMenu } = useCreateMenu();
  const { updateMenu } = useUpdateMenu();
  const { deleteMenu } = useDeleteMenu();
  const { createMenuFile } = useCreateMenuFile();
  const { fileUpload } = useFileUpload();
  // const { fileUpload: fileMultiUpload } = useFileMultiUpload();

  const { createProductCategory } = useCreateProductCategory();
  const { updateProductCategory } = useUpdateProductCategory();

  /* ===== Data ===== */
  const userInfo = useUserStack();

  // 등급 전체 조회
  const { data: Grades } = useGetGrades(
    { is_enable: 'Y' },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );

  // 메뉴 전체 조회
  const { data: Menus } = useGetMenus({
    join_tables: '["menu_file"]',
  });
  // 메뉴 카테고리 전체 조회
  const { data: Categories } = useGetMenuCategories();
  // 데이터 리패치
  const getQueryKey = {
    MENU: () =>
      menuQueryKeys.getMenus({
        join_tables: '["menu_file"]',
      }),
    CATEGORY: () => productCategoryQueryKeys.getMenuCategories(),
  };
  /* ===== State ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 정제된 데이터
  const [data, setData] = React.useState(null);
  // 모달
  const [isOpened, setIsOpened] = React.useState(false);
  const [modalType, setModalType] = React.useState(false);
  const [modalData, setModalData] = React.useState({
    menu: {},
    category: {},
  });
  // 메뉴 파일
  const [files, setFiles] = React.useState([]);
  const [thumbnailFiles, setThumbnailFiles] = React.useState([]);
  // 메뉴 옵션
  const [options, setOptions] = React.useState([]);
  // 영양 정보 옵션
  const [nutritionalInfo, setNutritionalInfo] = React.useState([]);
  // 순서 편집 데이터
  // const [orderList, setOrderList] = React.useState([]);
  // 리패칭
  const [isRefetch, setIsRefetch] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));

    if (data?.menu_file?.length > 0) {
      const thumbnailFiles = data.menu_file.filter(
        (file) => file.type === 'THUMBNAIL'
      );
      const otherFiles = data.menu_file.filter(
        (file) => file.type !== 'THUMBNAIL'
      );

      setThumbnailFiles(thumbnailFiles);
      setFiles(otherFiles);
    } else {
      setThumbnailFiles([]);
      setFiles([]);
    }
    setOptions(data?.menu_option || []);
    setNutritionalInfo(data?.nutritional_information || []);
    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = (t, v, o) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
      o: { ...newData },
    };

    setModalData(d);
  };

  /**
   * 메뉴 삭제
   * --
   */
  const handleDeleteSelected = async (idx) => {
    if (!idx) {
      MessageAlert.warning('삭제할 항목을 선택해주세요.');
      return;
    }
    try {
      setIsLoading(true);

      const { status } = await deleteMenu(idx);

      if (status === 200) {
        MessageAlert.success('선택한 메뉴가 삭제되었습니다.');
        const queryKey = getQueryKey['MENU']();
        queryClient.invalidateQueries({
          queryKey: queryKey.queryKey,
        });
        setSelectedItem(null);
        setIsRefetch(true);
      } else {
        MessageAlert.error(
          '메뉴와 적용된 다른 데이터를 확인하고 다시 시도해주세요.'
        );
      }
    } catch (err) {
      MessageAlert.error(
        '메뉴와 적용된 다른 데이터를 확인하고 다시 시도해주세요.'
      );
      console.error('[DELETE EVENT ERROR MENU]:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 데이터 생성
   * --
   * @param {string} t
   * @param {string} s
   * @returns
   */
  const handleSubmit = async (t, s = 'CREATE') => {
    // console.log('modalData', modalData);
    // console.log('nritu', nutritionalInfo);
    // console.log('options', options);
    // 생성 타입 대문자 변환
    const type = t.toLocaleUpperCase();
    // 함수 타입 대문자 변환
    const submit = s.toLocaleUpperCase();

    // 생성 타입 정의
    const submitType = {
      MENU: '메뉴',
      CATEGORY: '카테고리',
      CREATE: '생성',
      UPDATE: '수정',
    };

    // NOTE: 데이터 유효성 검사 및 데이터 리턴
    const data = handleValidateCheck(type);
    if (!data) return;

    // NOTE: 생성/수정 함수 정의
    const submitFunc = {
      MENU: {
        CREATE: createMenu,
        UPDATE: updateMenu,
      },
      CATEGORY: {
        CREATE: createProductCategory,
        UPDATE: updateProductCategory,
      },
    };

    try {
      // 로딩 처리
      setIsLoading(true);

      // 데이터 생성/수정
      const { status, data: createdMenuData } = await submitFunc[type][submit]({
        ...data,
      });

      // }
      if (status === 200) {
        // 파일 업로드 처리
        if (type === 'MENU' && files && files.length > 0) {
          // 파일 리스트 정제
          const Newfiles = [
            ...thumbnailFiles?.map((file) => {
              file.category = 'THUMBNAIL';
              return file;
            }),
            ...files,
          ];

          const menu_id = createdMenuData.menu_id; // 생성된 메뉴 ID
          const fileSavePromises = Newfiles.map(async (file, index) => {
            const timestamp = Date.now();
            const fileExtension = file?.name?.split('.').pop();
            const baseName = file?.name?.replace(`.${fileExtension}`, '');
            const newBaseName = baseName?.replace(/[^a-zA-Z0-9_.-]/g, '_');
            const fileName = `jm_coffee/menus/${menu_id}/${newBaseName}_${timestamp}.${fileExtension}`;

            if (file.name) {
              // 각 파일을 개별적으로 업로드
              const uploadResponse = await fileUpload({
                file,
                file_name: fileName,
              });

              if (uploadResponse) {
                // 파일 정보 저장
                const menuFileInfo = {
                  menu_id,
                  file_name: `${newBaseName}_${timestamp}.${fileExtension}`,
                  file_url: fileName,
                  type: file.category,
                };
                return createMenuFile(menuFileInfo);
              }
            }
          });

          // 모든 파일 저장이 완료될 때까지 대기
          await Promise.all(fileSavePromises);
        }

        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`
        );
        const queryKey = getQueryKey[type]();
        queryClient.invalidateQueries({
          queryKey: queryKey.queryKey,
        });
        setModalData((prev) => ({ ...prev, t: {} }));
        setIsOpened(!isOpened);
        // setSelectedItem(null);
        setIsRefetch(true);
        return;
      }

      MessageAlert.warning(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`
      );
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      // 로딩 처리
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  };
  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = (t) => {
    if (t === 'MENU') {
      const select_menu_length =
        data?.filter(
          (i) =>
            i.product_category_id === modalData?.category?.product_category_id
        )?.[0]?.menus?.length + 1;

      // 메뉴 유효성 검사
      const {
        menu_name,
        price,
        menu_content,
        temperature_option,
        is_show,
        type,
        exchange_return_policy,
        is_present,
        present_information,
      } = modalData?.menu;
      // 옵션 데이터 처리: option_type이 선택되지 않았을 경우 COUNT로 설정
      const processedOptions = options.map((option) => ({
        ...option,
        type: option.type,
        option_type: option.option_type || 'COUNT', // option_type이 없으면 COUNT로 설정
        option: option.option.map((subOption) => ({
          ...subOption,
          option_type: option.option_type || 'COUNT', // 하위 옵션도 마찬가지로 처리
        })),
      }));

      if (!menu_name) {
        MessageAlert.warning('메뉴명을 입력해주세요.');
        return false;
      }
      if (price === null || price === undefined || price === '') {
        MessageAlert.warning('단가를 입력해주세요.');
        return false;
      }
      if (!type) {
        MessageAlert.warning('메뉴타입을 선택해주세요.');
        return false;
      }
      if (!is_show) {
        MessageAlert.warning('노출여부를 선택해주세요.');
        return false;
      }
      if (!menu_content) {
        MessageAlert.warning('메뉴설명을 입력해주세요.');
        return false;
      }
      if (menu_content?.length > 200) {
        MessageAlert.warning('메뉴설명의 길이를 확인해주세요');
        return false;
      }
      if (exchange_return_policy?.length > 200) {
        MessageAlert.warning('교환/반품 안내의 길이를 확인해주세요');
        return false;
      }
      if (type === 'GENERAL') {
        if (!temperature_option) {
          MessageAlert.warning('온도옵션을 선택해주세요.');
          return false;
        }
      }
      if (is_present === 'Y') {
        if (present_information?.present_expired_date === '') {
          MessageAlert.warning('선물유효기간을 선택해주세요.');
          return false;
        }
        if (present_information?.present_postponed_deadline === '') {
          MessageAlert.warning('최대연장기간을 선택해주세요.');
          return false;
        }
      }

      return {
        ...modalData?.menu,
        num: 100,
        product_category_id: modalData?.menu?.product_category_id,
        application_status: 'APPROVE',
        temperature_option: type !== 'GENERAL' ? 'OTHER' : temperature_option,
        nutritional_information: nutritionalInfo,
        order: select_menu_length,
        menu_option: processedOptions,
        is_present: modalData?.menu?.is_present || 'N',
        present_information:
          modalData?.menu?.is_present === 'Y'
            ? {
                present_expired_date:
                  modalData?.menu?.present_information?.present_expired_date,
                present_postponed_deadline:
                  modalData?.menu?.present_information
                    ?.present_postponed_deadline,
              }
            : null,
      };
    } else if (t === 'CATEGORY') {
      // 카테고리 유효성 검사
      const { product_category_name } = modalData?.category;

      if (!product_category_name) {
        MessageAlert.warning('카테고리명을 입력해주세요.');
        return false;
      }
      if (product_category_name?.length > 20) {
        MessageAlert.warning('카테고리명의 길이를 확인해주세요.');
        return false;
      }

      return {
        ...modalData?.category,
        application_status: 'APPROVE',
        type: 'MENU',
        order: Categories?.data?.length + 1,

        // NOTE: 쇼브매지idx 추가
        shop_information_id: userInfo?.shop_information_id,
      };
    }
  };

  /**
   * 메뉴 옵션 바인딩 함수
   * --
   * @param {*} data
   * @param {number} index
   */
  const handleOption = (data, index) => {
    let newData = [...options];

    newData[index] = data;
    setOptions(newData);
  };

  /**
   * 영양 정보 상태 업데이트 함수
   * --
   * @param {string} action - 수행할 작업 ('add', 'update', 'remove')
   * @param {object} [data] - 업데이트할 데이터 객체 (예: { nutritional_title: '칼로리', nutritional_amount: '100', nutritional_type: 'kcal' })
   * @param {number} [index] - 업데이트 또는 삭제할 항목의 인덱스
   */
  const handleNutritionalInfo = (action, data = {}, index = null) => {
    setNutritionalInfo((prevInfo) => {
      let updatedInfo = [...prevInfo];

      if (action === 'add') {
        // 새 항목 추가
        updatedInfo.push({
          nutritional_info_id: prevInfo.length + 1, // 유일한 ID 할당
          nutritional_title: '',
          nutritional_type: 'mg',
          nutritional_amount: 0,
        });
      } else if (action === 'update' && index !== null) {
        // 특정 인덱스의 항목 업데이트
        updatedInfo[index] = { ...updatedInfo[index], ...data };
      } else if (action === 'remove' && index !== null) {
        // 특정 인덱스의 항목 삭제
        updatedInfo.splice(index, 1);
      }

      // 업데이트된 영양 정보를 상태에 반영
      return updatedInfo;
    });
  };

  /* ===== Hooks ===== */
  // /**
  //  * 데이터 정제
  //  * --
  //  */
  // useEffect(() => {
  //   const newData = Categories?.data?.map((category) => ({
  //     ...category,
  //     menus: Menus?.data?.filter(
  //       (menu) =>
  //         menu?.product_category_id === category?.product_category_id &&
  //         menu?.shop_information_id === null
  //     ),
  //   }));
  //   setData(newData);
  // }, [Menus, Categories]);

  useEffect(() => {
    if (!Categories?.data || !Menus?.data) return; // 데이터가 없으면 실행하지 않음
    // 상위 카테고리 및 하위 카테고리 그룹화
    const categoryMap = Categories.data.reduce((acc, category) => {
      if (!category.parents_id) {
        acc[category.product_category_id] = {
          ...category,
          subCategories: [],
          menus: [], // 상위 카테고리의 메뉴 배열 초기화
        };
      } else {
        const parentCategory = acc[category.parents_id];
        if (parentCategory) {
          parentCategory.subCategories.push({
            ...category,
            menus: [], // 하위 카테고리의 메뉴 배열 초기화
          });
        }
      }
      return acc;
    }, {});

    // 메뉴를 해당 카테고리 또는 하위 카테고리에 매핑
    Menus.data.forEach((menu) => {
      if (menu.application_status !== 'APPROVE') return;
      const category = categoryMap[menu.product_category_id];
      if (category) {
        // 상위 카테고리의 메뉴 추가
        category.menus.push(menu);
      } else {
        // 하위 카테고리의 메뉴 추가
        const parentCategory = Object.values(categoryMap).find((c) =>
          c.subCategories.some(
            (subCategory) =>
              subCategory.product_category_id === menu.product_category_id
          )
        );
        if (parentCategory) {
          const subCategory = parentCategory.subCategories.find(
            (subCategory) =>
              subCategory.product_category_id === menu.product_category_id
          );
          if (subCategory) {
            subCategory.menus.push(menu);
          }
        }
      }
    });

    // 카테고리 데이터를 배열로 변환하여 업데이트
    const newData = Object.values(categoryMap)?.sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );

    // 하위 카테고리를 정렬
    Object.values(categoryMap).forEach((category) => {
      category.subCategories.sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // 기존 데이터와 비교 후 상태 업데이트
    if (JSON.stringify(newData) !== JSON.stringify(data)) {
      setData(newData);
    }
    if (selectedItem) {
      setSelectedItem((prev) =>
        Menus.data?.find((i) => i?.menu_id === prev?.menu_id)
      );
    }
  }, [Menus, Categories?.data, data, isRefetch]);
  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <MenuListPresenter
        menuData={data}
        isOpened={isOpened}
        modalData={modalData}
        modalType={modalType}
        options={options}
        nutritionalInfo={nutritionalInfo}
        thumbnailFiles={thumbnailFiles}
        files={files}
        isRefetch={isRefetch}
        onOpen={handleModal}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onOptionList={setOptions}
        onOptionChange={handleOption}
        onNuritionalChange={handleNutritionalInfo}
        onFileChange={setFiles}
        onThumbnailFileChange={setThumbnailFiles}
        onDelete={handleDeleteSelected}
        selectedItem={selectedItem}
        onSelectedItem={setSelectedItem}
        //
        grades={Grades}
      />
    </>
  );
};

export default MenuListContainer;
